import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Mail, MailX } from "lucide-react";

const TYPE_META = {
  digest: { label: "Weekly digest", desc: "Monday team-progress summary to workspace admins" },
  nudge: { label: "Nudges", desc: "Manual 'resume onboarding' emails to trainees" },
  welcome: { label: "Welcome emails", desc: "Sent on registration or when an admin creates a user (includes credentials)" },
  certificate: { label: "Certificates", desc: "Certificate of Completion email with PDF attached" },
  trial: { label: "Trial reminders", desc: "Day-10 upgrade nudges for trial workspaces" },
};

const REASON_LABELS = {
  workspace_paused: "All emails paused",
  recipient_muted: "Recipient muted",
  monthly_cap_reached: "Monthly cap reached",
};

export default function EmailsTab() {
  const [settings, setSettings] = useState(null);
  const [log, setLog] = useState({ entries: [], by_type_this_month: {} });
  const [saving, setSaving] = useState(false);
  const [capInput, setCapInput] = useState("");

  const load = () => {
    api.get("/admin/email-settings").then((r) => { setSettings(r.data); setCapInput(r.data.monthly_cap ?? ""); }).catch(() => {});
    api.get("/admin/email-log").then((r) => setLog(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/email-settings", {
        enabled: next.enabled, types: next.types, monthly_cap: next.monthly_cap ?? null,
      });
      setSettings(data);
      setCapInput(data.monthly_cap ?? "");
      toast.success(next.enabled ? "Email settings saved" : "All outgoing emails paused for this workspace");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  if (!settings) return <div className="text-sm text-neutral-500">Loading email settings…</div>;

  return (
    <div className="space-y-8" data-testid="emails-tab">
      {/* Counters */}
      <div className="flex flex-wrap gap-6 text-sm">
        <div data-testid="emails-sent-counter"><span className="font-display text-2xl">{settings.sent_this_month}</span> <span className="text-neutral-500">sent in {settings.month}</span></div>
        <div data-testid="emails-blocked-counter"><span className="font-display text-2xl">{settings.blocked_this_month}</span> <span className="text-neutral-500">blocked by your controls</span></div>
        {settings.monthly_cap && (
          <div data-testid="emails-cap-counter">
            <span className={`font-display text-2xl ${settings.cap_reached ? "text-[#8B2E2E]" : ""}`}>{settings.sent_this_month}/{settings.monthly_cap}</span>{" "}
            <span className="text-neutral-500">of monthly cap</span>
          </div>
        )}
      </div>

      {settings.cap_reached && (
        <div className="border-2 border-[#8B2E2E] bg-[#8B2E2E]/5 p-4 text-sm" data-testid="cap-reached-banner">
          <span className="font-medium text-[#8B2E2E]">Monthly cap reached.</span>{" "}
          <span className="text-neutral-600">Campaign emails are paused until next month. Raise or clear the cap below to resume. Security emails still send.</span>
        </div>
      )}

      {/* Master switch */}
      <div className={`border-2 p-5 flex items-center justify-between gap-4 ${settings.enabled ? "border-[color:var(--brand-primary)] bg-white" : "border-[#8B2E2E] bg-[#8B2E2E]/5"}`}>
        <div className="flex items-start gap-4">
          {settings.enabled ? <Mail size={20} className="text-[var(--brand-primary)] mt-0.5" /> : <MailX size={20} className="text-[#8B2E2E] mt-0.5" />}
          <div>
            <div className="font-medium">{settings.enabled ? "Emails are on" : "All emails paused"}</div>
            <div className="text-sm text-neutral-500">
              {settings.enabled
                ? "Campaign emails send according to the toggles below. Security emails (temp passwords) always send."
                : "No campaign emails will leave this workspace. Security emails (temp passwords) still send."}
            </div>
          </div>
        </div>
        <Switch checked={settings.enabled} disabled={saving}
                onCheckedChange={(v) => save({ ...settings, enabled: v })}
                data-testid="emails-master-switch" />
      </div>

      {/* Per-type toggles */}
      <div className="border hairline divide-y divide-neutral-100 bg-white">
        {Object.entries(TYPE_META).map(([key, meta]) => {
          const counts = log.by_type_this_month?.[key];
          return (
            <div key={key} className={`p-4 flex items-center justify-between gap-4 ${!settings.enabled ? "opacity-50" : ""}`}>
              <div>
                <div className="text-sm font-medium">{meta.label}</div>
                <div className="text-xs text-neutral-500">{meta.desc}</div>
                {counts && <div className="text-[11px] font-mono text-neutral-400 mt-1">{counts.sent} sent · {counts.blocked} blocked this month</div>}
              </div>
              <Switch checked={settings.types[key] !== false} disabled={saving || !settings.enabled}
                      onCheckedChange={(v) => save({ ...settings, types: { ...settings.types, [key]: v } })}
                      data-testid={`emails-type-switch-${key}`} />
            </div>
          );
        })}
        <div className="p-4 flex items-center justify-between gap-4 bg-[color:var(--brand-surface2)]/40">
          <div>
            <div className="text-sm font-medium">Security emails</div>
            <div className="text-xs text-neutral-500">Temporary passwords after an approved reset — always delivered, cannot be paused.</div>
          </div>
          <span className="label-eyebrow text-[var(--brand-primary)]">Always on</span>
        </div>
      </div>

      {/* Monthly budget cap */}
      <div className="border hairline bg-white p-5" data-testid="cap-section">
        <div className="text-sm font-medium">Monthly email budget</div>
        <div className="text-xs text-neutral-500 mt-1">
          Auto-pause all campaign emails once this many have been sent in a calendar month. You'll receive an alert email when the cap is hit. Leave blank for unlimited.
        </div>
        <form className="mt-3 flex flex-wrap items-center gap-2" onSubmit={(e) => {
          e.preventDefault();
          const cap = capInput === "" ? null : parseInt(capInput, 10);
          if (cap !== null && (!Number.isFinite(cap) || cap < 1)) { toast.error("Cap must be a positive number"); return; }
          save({ ...settings, monthly_cap: cap });
        }}>
          <input type="number" min="1" placeholder="Unlimited" value={capInput}
                 onChange={(e) => setCapInput(e.target.value)}
                 className="border hairline bg-white px-3 py-2 text-sm w-36 focus:outline-none focus:border-[color:var(--brand-primary)]"
                 data-testid="cap-input" />
          <button type="submit" disabled={saving} className="btn-primary text-sm" data-testid="cap-save-btn">Save cap</button>
          {settings.monthly_cap && (
            <button type="button" disabled={saving} className="btn-ghost text-sm" data-testid="cap-clear-btn"
                    onClick={() => { setCapInput(""); save({ ...settings, monthly_cap: null }); }}>
              Remove cap
            </button>
          )}
        </form>
      </div>

      {/* Log */}
      <div>
        <div className="label-eyebrow mb-3">Email log · most recent first</div>
        {log.entries.length === 0 ? (
          <div className="text-sm text-neutral-500">No emails logged yet for this workspace.</div>
        ) : (
          <div className="border hairline overflow-x-auto bg-white">
            <table className="w-full text-sm" data-testid="email-log-table">
              <thead className="bg-[color:var(--brand-surface2)]">
                <tr className="text-left label-eyebrow">
                  <th className="p-3">When</th>
                  <th className="p-3">To</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Subject</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {log.entries.map((e) => (
                  <tr key={e.id} className="border-t hairline" data-testid={`email-log-row-${e.id}`}>
                    <td className="p-3 font-mono text-xs text-neutral-500 whitespace-nowrap">{(e.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="p-3 font-mono text-xs">{e.to}</td>
                    <td className="p-3"><span className="label-eyebrow text-[var(--brand-primary)]">{e.type}</span></td>
                    <td className="p-3 text-neutral-600 max-w-xs truncate">{e.subject}</td>
                    <td className="p-3">
                      {e.sent ? (
                        <span className="inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#2B6E4F]/10 text-[#2B6E4F]">Sent</span>
                      ) : (
                        <span className="inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#8B2E2E]/10 text-[#8B2E2E]" title={e.reason || ""}>
                          {e.blocked ? (REASON_LABELS[e.reason] || e.reason || "Blocked") : "Failed"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
